/**
 * Sign-in: one question, and the portal takes it from there.
 *
 *   account   (this device signed in before)  "Continue" the same way as last time — or another account
 *   identify  the work e-mail → POST /api/auth/start; the BFF decides:
 *               a company-SSO domain → that IdP (the address passed as login_hint)
 *               anyone else          → a code (the same answer whether or not one was sent)
 *             passkeys need no choice: offered in the field's autofill, and as a quiet link
 *   code      8 digits → email-verify; the BFF now says what a passkey means for this person
 *   passkey   required (how they get in) · recommended (admin, passkey-only company) · optional (skip the code
 *             next time — only where the device holds passkeys, and not again after "Not now")
 *
 * Nobody is asked to know which method is theirs. Nothing about an address is revealed before its mailbox is
 * proven: the only branch before the code is the domain's SSO, which is public. An invitation links here with
 * `?email=`; links from before (`?mode=…&email=`) still fill the address in.
 */

import { Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import type { PasskeyAdvice } from '@portal/contracts'
import type { AuthenticationResponseJSON } from '@simplewebauthn/browser'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  emailVerify,
  getAuthMethods,
  loginOptions,
  loginVerify,
  registerOptions,
  registerVerify,
  signInStart,
  ssoStartUrl
} from '../api/auth'
import { PortalApiError } from '../api/portal-client'
import {
  classifyPasskeyError,
  hasPlatformAuthenticator,
  isAbort,
  runAuthentication,
  runRegistration,
  supportsPasskeys
} from '../auth/passkey'
import {
  forgetRemembered,
  passkeyOfferSnoozed,
  type RememberedAccount,
  readRemembered,
  remember,
  type SignInMethod,
  snoozePasskeyOffer
} from '../auth/remembered-account'
import { passkeyStepFor, prefillFromLink } from '../auth/sign-in-flow'
import { useSession } from '../auth/useSession'
import { AccountStep } from '../components/login/AccountStep'
import { CodeStep } from '../components/login/CodeStep'
import { IdentifyStep } from '../components/login/IdentifyStep'
import { PasskeyStep } from '../components/login/PasskeyStep'
import { describeApiError, LanguageSwitcher, type Messages, useI18n } from '../i18n'

type Step =
  | { kind: 'account'; account: RememberedAccount }
  | { kind: 'identify' }
  | { kind: 'code'; email: string; tokenId: string; sentAt: number; resent: boolean }
  | { kind: 'passkey'; email: string; advice: PasskeyAdvice; mode: 'required' | 'recommended' | 'optional' }
  | { kind: 'redirecting'; label: string }

/** A message is kept as a function of the catalog, so switching language re-renders it in the new one. */
type Message = (t: Messages) => string

interface FromState {
  from?: string
}

/** Friendly copy for the `oidcError` reasons the BFF redirects back with. */
function oidcErrorMessage(reason: string): Message {
  return (t) => {
    switch (reason) {
      case 'access_denied':
        return t.login.oidcAccessDenied
      case 'not_provisioned':
        return t.login.oidcNotProvisioned
      case 'tenant_not_allowed':
        return t.login.oidcTenantNotAllowed
      case 'role_not_assigned':
        return t.login.oidcRoleNotAssigned
      case 'email_missing':
      case 'email_unverified':
        return t.login.oidcEmailMissing
      case 'email_not_allowed':
        return t.login.oidcEmailNotAllowed
      case 'sso_unknown':
        return t.login.oidcSsoUnknown
      case 'expired':
      case 'state_mismatch':
      case 'invalid_callback':
        return t.login.oidcExpired
      case 'disabled':
        return t.login.oidcDisabled
      default:
        return t.login.oidcGenericError
    }
  }
}

/** A failed call or passkey ceremony, localized. */
function failure(err: unknown): Message {
  return (t) => {
    // No session is in play on this page: a 401 is a passkey the BFF does not know (or did not verify).
    if (err instanceof PortalApiError && err.status === 401) return t.login.passkeyNotRecognized
    if (err instanceof PortalApiError) return describeApiError(t, err)
    const { kind, message } = classifyPasskeyError(err)
    if (kind === 'dismissed') return t.login.passkeyDismissed
    return message ?? t.login.passkeyGenericError
  }
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, locale } = useI18n()
  const { refresh } = useSession()
  const dest = (location.state as FromState | null)?.from ?? '/'

  // Read the link once, then clean the URL (a reload must not replay an error or an invitation).
  const [searchParams, setSearchParams] = useSearchParams()
  const [link] = useState(() => ({
    prefill: prefillFromLink(searchParams).email,
    oidcError: searchParams.get('oidcError'),
    orgSso: searchParams.get('sso')
  }))
  useEffect(() => {
    if (searchParams.has('email') || searchParams.has('mode') || searchParams.has('oidcError')) {
      const kept = new URLSearchParams(searchParams)
      for (const k of ['email', 'mode', 'oidcError']) kept.delete(k)
      setSearchParams(kept, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const [step, setStep] = useState<Step>(() => {
    let account = readRemembered()
    // An SSO attempt that came back with an error never signed in: do not offer it again as "the" account.
    if (account?.pending && link.oidcError) {
      forgetRemembered()
      account = null
    }
    return account && !link.prefill ? { kind: 'account', account } : { kind: 'identify' }
  })
  const [email, setEmail] = useState(link.prefill)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<Message | null>(() => (link.oidcError ? oidcErrorMessage(link.oidcError) : null))
  const [passkeyFailed, setPasskeyFailed] = useState(false)
  // A ceremony the page itself started can outlive the step that started it; results after a step change are ignored.
  const stepRef = useRef(step)
  stepRef.current = step

  const methods = useQuery({ queryKey: ['auth-methods'], queryFn: getAuthMethods, staleTime: 300_000 })
  const legacy = methods.data?.oidc ?? null

  const go = (next: Step) => {
    setError(null)
    setStep(next)
  }

  const finish = async (account: { email: string; method: SignInMethod }) => {
    remember(account)
    await refresh()
    navigate(dest, { replace: true })
  }

  /** Run `fn` with the spinner on and errors caught into the page's message. */
  const attempt = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      if (!isAbort(err)) setError(() => failure(err))
    } finally {
      setBusy(false)
    }
  }

  // ── The one question ──────────────────────────────────────────────────────
  const start = (address: string, opts: { resend?: boolean } = {}) =>
    attempt(async () => {
      const res = await signInStart({ email: address, locale })
      if (res.next === 'sso') {
        remember({ email: address, method: 'sso', sso: res.provider, pending: true })
        go({ kind: 'redirecting', label: res.provider.label })
        window.location.assign(ssoStartUrl(res.provider.slug, dest, address))
        return
      }
      setEmail(address)
      go({ kind: 'code', email: address, tokenId: res.tokenId, sentAt: Date.now(), resent: Boolean(opts.resend) })
    })

  // ── Passkeys ──────────────────────────────────────────────────────────────
  const completePasskey = async (authResp: AuthenticationResponseJSON) => {
    const { user } = await loginVerify({ authResp })
    await finish({ email: user.email, method: 'passkey' })
  }

  /** The modal ceremony: this device, a phone (QR) or a security key. */
  const passkeySignIn = async () => {
    setBusy(true)
    setError(null)
    setPasskeyFailed(false)
    try {
      const options = await loginOptions()
      await completePasskey(await runAuthentication(options))
    } catch (err) {
      if (isAbort(err)) return
      setPasskeyFailed(true)
      // Closing the prompt on the e-mail step usually means "no passkey here": point back at the e-mail.
      const dismissedOnIdentify = stepRef.current.kind === 'identify' && classifyPasskeyError(err).kind === 'dismissed'
      setError(() => (dismissedOnIdentify ? (tt: Messages) => tt.login.noPasskeyHere : failure(err)))
    } finally {
      setBusy(false)
    }
  }

  // ── The code ──────────────────────────────────────────────────────────────
  const verify = async (code: string): Promise<boolean> => {
    if (step.kind !== 'code') return false
    const { email: address, tokenId } = step
    setBusy(true)
    setError(null)
    try {
      const res = await emailVerify({ tokenId, code })
      if (!('signedIn' in res)) {
        // Only the `signin` purpose reaches this page; an older shape still means "signed in".
        await finish({ email: address, method: 'code' })
        return true
      }
      const next = passkeyStepFor(res.passkey, {
        canUsePasskeys: supportsPasskeys(),
        platformAuthenticator: await hasPlatformAuthenticator(),
        snoozed: passkeyOfferSnoozed(address)
      })
      if (next === 'skip') {
        await finish({ email: address, method: 'code' })
        return true
      }
      go({ kind: 'passkey', email: address, advice: res.passkey, mode: next })
      return true
    } catch (err) {
      setError(() => failure(err))
      return false
    } finally {
      setBusy(false)
    }
  }

  // ── After the code: the passkey step ──────────────────────────────────────
  const createPasskey = () => {
    if (step.kind !== 'passkey') return
    const { email: address, mode } = step
    return attempt(async () => {
      try {
        const attResp = await runRegistration(await registerOptions({}))
        await registerVerify({ attResp })
        await finish({ email: address, method: 'passkey' })
      } catch (err) {
        if (isAbort(err)) return
        // This device already holds a passkey for the account: use it instead of making another.
        if (classifyPasskeyError(err).kind === 'already-exists') {
          await completePasskey(await runAuthentication(await loginOptions()))
          return
        }
        // The window the code opened (5 minutes) has closed.
        if (err instanceof PortalApiError && err.status === 401) {
          if (mode === 'required') {
            go({ kind: 'identify' })
          }
          setError(() => (tt: Messages) => tt.login.passkeyWindowExpired)
          return
        }
        throw err
      }
    })
  }

  const notNow = () => {
    if (step.kind !== 'passkey') return
    if (step.mode === 'optional') snoozePasskeyOffer(step.email)
    void attempt(() => finish({ email: step.email, method: 'code' }))
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const heading: { title: string; lead?: string } = (() => {
    switch (step.kind) {
      case 'account':
        return { title: t.login.welcomeBack }
      case 'identify':
        return { title: t.login.title, lead: t.login.identifyLead }
      case 'code':
        return { title: t.login.codeTitle }
      case 'passkey':
        return {
          title:
            step.mode === 'required'
              ? t.login.passkeyRequiredTitle
              : step.mode === 'recommended'
                ? t.login.passkeyRecommendedTitle
                : t.login.passkeyOptionalTitle
        }
      case 'redirecting':
        return { title: t.login.redirecting(step.label) }
    }
  })()

  const canGoBack = step.kind === 'code' || (step.kind === 'passkey' && step.mode === 'required')

  return (
    <div className="portal-center">
      <div className="login-card card">
        <div className="login-head">
          {canGoBack && (
            <button
              type="button"
              className="login-back"
              aria-label={t.common.back}
              disabled={busy}
              onClick={() => go({ kind: 'identify' })}
            >
              <Icon name="chevron" size={16} />
            </button>
          )}
          <div className="login-logo" aria-hidden="true">
            <Icon name="bolt" size={22} />
          </div>
          <p className="login-brand">{t.login.subtitle}</p>
          <h1>{heading.title}</h1>
          {heading.lead && <p className="muted">{heading.lead}</p>}
        </div>

        <div aria-live="assertive">
          {error && (
            <Notice kind="danger" className="login-notice">
              {error(t)}
            </Notice>
          )}
        </div>

        {step.kind === 'account' && (
          <AccountStep
            account={step.account}
            busy={busy}
            showCodeFallback={passkeyFailed}
            onContinue={() => {
              const { account } = step
              if (account.method === 'passkey') void passkeySignIn()
              else if (account.method === 'sso' && account.sso) {
                setBusy(true)
                go({ kind: 'redirecting', label: account.sso.label })
                window.location.assign(ssoStartUrl(account.sso.slug, dest, account.email))
              } else void start(account.email)
            }}
            onCodeInstead={() => void start(step.account.email)}
            onOtherAccount={() => {
              setEmail('')
              go({ kind: 'identify' })
            }}
            onForget={() => {
              forgetRemembered()
              setEmail('')
              go({ kind: 'identify' })
            }}
          />
        )}

        {step.kind === 'identify' && (
          <IdentifyStep
            email={email}
            onEmailChange={setEmail}
            busy={busy}
            onContinue={(address) => void start(address)}
            onPasskey={passkeySignIn}
            onAutofill={(resp) => attempt(() => completePasskey(resp))}
            {...(link.orgSso
              ? {
                  orgSso: () => {
                    setBusy(true)
                    window.location.assign(ssoStartUrl(link.orgSso ?? '', dest))
                  }
                }
              : {})}
            {...(legacy
              ? {
                  legacySso: {
                    label: legacy.label,
                    start: () => {
                      setBusy(true)
                      window.location.assign(`/auth/oidc/start?returnTo=${encodeURIComponent(dest)}`)
                    }
                  }
                }
              : {})}
          />
        )}

        {step.kind === 'code' && (
          <CodeStep
            email={step.email}
            sentAt={step.sentAt}
            resent={step.resent}
            busy={busy}
            onSubmit={verify}
            onResend={() => void start(step.email, { resend: true })}
            onDifferentEmail={() => go({ kind: 'identify' })}
            onPasskey={() => void passkeySignIn()}
          />
        )}

        {step.kind === 'passkey' && (
          <PasskeyStep
            mode={step.mode}
            advice={step.advice}
            busy={busy}
            onCreate={() => void createPasskey()}
            onUseExisting={() => void passkeySignIn()}
            onNotNow={notNow}
          />
        )}

        {step.kind === 'redirecting' && (
          <div className="login-body login-redirecting">
            <Spinner size={20} />
          </div>
        )}

        <div className="login-lang">
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  )
}
